/**
 * The manifest, as the page reads it (§ G16 + the 2026-08-18 redesign).
 *
 * Two jobs, both pure and both testable without a browser:
 *
 *  1. `manifestSections` — regroup the payload BY DIRECTION. The API returns
 *     people, each with legs, because that is the shape the roster needs. Wade
 *     asked for the itinerary's shape instead: *"all outbound on top and returns
 *     on bottom"*. So the name moves INTO the row and the rows are ordered by
 *     when they travel.
 *  2. `manifestText` — the same page as plain text for "Copy as text".
 *
 * ⚠️ Both mirror `backend/src/services/groupManifest.ts`, which does the same two
 * things server-side (the printed/emailed copy uses it). Two copies drift, so
 * both are tested against the same expectations — that duplication is deliberate
 * and predates this file.
 *
 * ⚠️ Nobody may fall out of the grouping. "Flying but no legs yet" and "not
 * flying at all" are DIFFERENT states (§ G33) and each has its own bucket;
 * collapsing either into "no flights" is exactly the bug G33 exists to kill.
 */
import type { GroupManifest, ManifestItem, ManifestLeg, ManifestPerson } from '@/lib/api';

export interface ManifestRow {
  person: ManifestPerson;
  leg: ManifestLeg;
}

export interface ManifestGroundRow {
  person: ManifestPerson;
  item: ManifestItem;
}

export interface ManifestSections {
  /** Every outbound leg in the group, earliest travel date first. */
  outbound: ManifestRow[];
  /** Every return leg, same ordering. */
  returns: ManifestRow[];
  /** § G33 — not flying, and therefore not missing anything. */
  ownTransport: ManifestPerson[];
  /** Flying, but nobody has planned a leg for them yet. */
  unplanned: ManifestPerson[];
  /** Hotels, dinners, activities — the middle of the trip. */
  ground: ManifestGroundRow[];
}

/** The date a leg actually travels on: the booking's, else what we shopped. */
export function legDate(l: ManifestLeg): string | null {
  return l.booking_departure_date || l.departure_date || null;
}

function byDateThenName(a: ManifestRow, b: ManifestRow): number {
  const da = legDate(a.leg) ?? '';
  const db = legDate(b.leg) ?? '';
  if (da !== db) return da < db ? -1 : 1;
  return a.person.display_name.localeCompare(b.person.display_name);
}

export function manifestSections(m: GroupManifest): ManifestSections {
  const outbound: ManifestRow[] = [];
  const returns: ManifestRow[] = [];
  const ownTransport: ManifestPerson[] = [];
  const unplanned: ManifestPerson[] = [];
  const ground: ManifestGroundRow[] = [];

  for (const person of m.people ?? []) {
    // `travel_mode` may be absent on a payload from an older backend; absent
    // means flying, which is the pre-G33 behaviour for every existing row.
    if ((person.travel_mode ?? 'flight') !== 'flight') {
      ownTransport.push(person);
    } else if ((person.legs ?? []).length === 0) {
      unplanned.push(person);
    }
    for (const leg of person.legs ?? []) {
      (leg.leg_role === 'return' ? returns : outbound).push({ person, leg });
    }
    for (const item of person.items ?? []) ground.push({ person, item });
  }

  outbound.sort(byDateThenName);
  returns.sort(byDateThenName);
  ground.sort((a, b) => {
    const da = a.item.day_date ?? '';
    const db = b.item.day_date ?? '';
    if (da !== db) return da < db ? -1 : 1;
    const ta = a.item.start_time ?? '99:99';
    const tb = b.item.start_time ?? '99:99';
    if (ta !== tb) return ta < tb ? -1 : 1;
    return a.person.display_name.localeCompare(b.person.display_name);
  });

  return { outbound, returns, ownTransport, unplanned, ground };
}

/**
 * Shift a YYYY-MM-DD date by whole days — the date a `+ before` / `+ after`
 * affordance opens on.
 *
 * Parsed as UTC on purpose. `new Date('2026-09-14')` is midnight UTC, but
 * `new Date(2026, 8, 14)` is midnight LOCAL, and in a negative-offset timezone
 * the two land on different days. A manifest read in Atlanta must not offer to
 * add a hotel on the 13th when the flight is on the 14th.
 */
export function shiftDate(ymd: string | null, days: number): string | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The date the meeting falls on, IN THE MEETING'S OWN CLOCK.
 *
 * ⚠️ `anchor_start_at` is a true instant, so slicing the ISO string would give
 * the UTC date — and a 7pm Atlanta meeting is already "tomorrow" in UTC. That is
 * the § G4 wall-clock-vs-instant error that once told an organizer somebody
 * missed a meeting they made. Formatted in `anchor_timezone`; UTC only when we
 * genuinely have no zone, which is the most we can honestly say.
 */
export function meetingDay(anchorStartAt: string | null, timezone: string | null): string | null {
  if (!anchorStartAt) return null;
  const d = new Date(anchorStartAt);
  if (Number.isNaN(d.getTime())) return null;
  try {
    // en-CA renders YYYY-MM-DD, which is the shape every date input wants.
    return d.toLocaleDateString('en-CA', { timeZone: timezone ?? 'UTC' });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** "Sarah Chen" / "Dana Reed (2 travellers)". Party size is never hidden. */
export function personLabel(p: ManifestPerson): string {
  return p.passengers > 1 ? `${p.display_name} (${p.passengers} travellers)` : p.display_name;
}

/**
 * A leg's booking state in words — for the text export and for a screen reader.
 *
 * FOUR states, not two. "booked, flight not recorded" is real (B4c) and must not
 * read the same as a leg whose ticket we can name.
 */
export function bookingWord(l: ManifestLeg): string {
  if (!l.booked) return '[not booked]';
  return l.flight ? '[BOOKED]' : '[BOOKED — flight not recorded]';
}

/** § G33 in plain words. Mirrors the backend's `travelModeText`. */
export function travelModeText(mode: string | null | undefined): string {
  switch (mode) {
    case 'car':
      return 'Driving';
    case 'train':
      return 'Taking the train';
    case 'bus':
      return 'Taking the bus';
    case 'other':
      return 'Own arrangements';
    default:
      return 'Flying';
  }
}

/**
 * Which flight to print for a leg, and whether it is a ticket or a target.
 *
 * ⚠️ The one rule this surface must never break: a BOOKED leg shows the booking
 * record's flight or NOTHING. Borrowing the focus flight would put a flight
 * nobody bought onto a document people travel on.
 */
export function legFlightLabel(l: ManifestLeg): { text: string; isFocus: boolean } | null {
  if (l.booked) return l.flight ? { text: l.flight, isFocus: false } : null;
  return l.focus_flight ? { text: l.focus_flight, isFocus: true } : null;
}

/**
 * The manifest as plain text, for pasting into an email or a chat.
 *
 * Deliberately plain: the people who most need this are the ones who will never
 * open the app, and a link they cannot follow is not sharing. Grouped by
 * direction so the pasted copy reads the same way as the page.
 */
export function manifestText(m: GroupManifest, whenLine: string | null): string {
  const s = manifestSections(m);
  const out: string[] = [m.event.name];
  if (whenLine) out.push(whenLine);
  out.push(m.summary, '');

  const legLine = (r: ManifestRow): string => {
    const flight = legFlightLabel(r.leg);
    return [
      `  ${personLabel(r.person)}`,
      `${r.leg.origin}→${r.leg.destination}`,
      legDate(r.leg) ?? '',
      flight ? (flight.isFocus ? `focus ${flight.text}` : flight.text) : '',
      r.leg.when ?? '',
      bookingWord(r.leg),
      r.leg.booking_ref ? `ref ${r.leg.booking_ref}` : '',
    ]
      .filter(Boolean)
      .join('  ');
  };

  out.push('GETTING THERE');
  if (s.outbound.length === 0) out.push('  nothing planned yet');
  for (const r of s.outbound) out.push(legLine(r));
  for (const p of s.unplanned) out.push(`  ${personLabel(p)}  no flights planned yet`);
  out.push('');

  if (s.ownTransport.length > 0) {
    out.push('OWN TRANSPORT');
    for (const p of s.ownTransport) {
      const label = travelModeText(p.travel_mode);
      out.push(`  ${personLabel(p)}  ${label}${p.travel_mode_note ? ` — ${p.travel_mode_note}` : ''}`);
    }
    out.push('');
  }

  if (s.ground.length > 0) {
    out.push('ON THE GROUND');
    for (const g of s.ground) {
      out.push(
        [
          `  ${g.person.display_name}`,
          g.item.title,
          g.item.day_date ?? '',
          g.item.start_time ?? '',
          g.item.location || g.item.city || '',
          g.item.confirmation ? `conf ${g.item.confirmation}` : '',
        ]
          .filter(Boolean)
          .join('  ')
      );
    }
    out.push('');
  }

  out.push('HEADING HOME');
  if (s.returns.length === 0) out.push('  nothing planned yet');
  for (const r of s.returns) out.push(legLine(r));
  out.push('');

  if (m.outstanding.length > 0) {
    out.push('Still to book:');
    for (const o of m.outstanding) {
      out.push(`  ${o.display_name}${o.unbooked > 0 ? ` — ${o.unbooked} leg(s)` : ' — no flights planned'}`);
    }
    out.push('');
  }

  out.push(`Total: ${m.amount_label}`);
  return out.join('\n');
}
